import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { getBaseUrl } from "../interface/getBaseUrl"
import axiosInstance from "../interface/axiosInstance"

interface Role {
  id: string
  name: string
  allowInKanban?: boolean
}

interface RolesState {
  roles: Role[]
  loading: boolean
  error: string | null
  lastFetched: number | null
}

const initialState: RolesState = {
  roles: [],
  loading: false,
  error: null,
  lastFetched: null
}

export interface RoleModel {
  id: number | string;
  name: string;
  allowInKanban?: boolean;
  permissions?: string[];
  status?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface UpdateRoleResponse {
  dataModel: RoleModel;
}

export const fetchRolesIfNeeded = createAsyncThunk(
  "roles/fetchRolesIfNeeded",
  async (_, { getState, dispatch }) => {
    const state = getState() as { roles: RolesState }
    const { lastFetched, roles } = state.roles

    if (roles.length > 0 && lastFetched && Date.now() - lastFetched < 5 * 60 * 1000) {
      return roles
    }

    return dispatch(fetchRoles()).unwrap()
  }
)

export const fetchRoles = createAsyncThunk(
  "roles/fetchRoles",
  async () => {
    const response = await axiosInstance.get(`${getBaseUrl(true)}/Role/GetRoles`)
    return response.data.dataModel
  }
)

export const addRole = createAsyncThunk<
  RoleModel,
  { name: string },
  { rejectValue: string } // Change rejectValue type to string for the error message
>(
  "roles/addRole",
  async (roleData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${getBaseUrl(true)}/Role/CreateRole`,
        roleData
      );
      // Do NOT also dispatch(fetchRoles()) here: the addRole.fulfilled reducer
      // pushes this role into state, and a concurrent full refetch would add it
      // a second time until the next page reload.
      return response.data.dataModel;
    } catch (err: any) {
      if (err.response && err.response.data) {
        const errorMessage = err.response.data.responseMessage || "An error occurred";
        return rejectWithValue(errorMessage);
      }
      return rejectWithValue(err.message || "Network error occurred");
    }
  }
);


export const updateRole = createAsyncThunk<
  RoleModel,
  { id: string; name: string; allowInKanban?: boolean },
  { rejectValue: any }
>(
  'roles/updateRole',
  async (roleData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.put<UpdateRoleResponse>(
        `${getBaseUrl(true)}/Role/UpdateRole`,
        roleData
      );
      return { ...roleData, ...response.data.dataModel };
    } catch (err: any) {
       if (err.response && err.response.data) {
        const errorMessage = err.response.data.responseMessage || "An error occurred";
        return rejectWithValue(errorMessage);
      }
      return rejectWithValue(err.message || "Network error occurred");
    }
  }
);

export const deleteRole = createAsyncThunk(
  "roles/deleteRole",
  async (id: string, { dispatch }) => {
    await axiosInstance.delete(`${getBaseUrl(true)}/Role/DeleteRole?id=${id}`)
    await dispatch(fetchRoles())
    return id
  }
)

const rolesSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch roles
      .addCase(fetchRoles.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loading = false
        state.roles = action.payload
        state.lastFetched = Date.now()
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || "Failed to fetch roles"
      })
      // Fetch roles if needed
      .addCase(fetchRolesIfNeeded.fulfilled, (state, action) => {
        state.roles = action.payload
        state.lastFetched = Date.now()
      })
      // Add role
      .addCase(addRole.fulfilled, (state, action) => {
        const newRole = { ...action.payload, id: String(action.payload.id) }
        // Idempotent: guard against a concurrent fetchRoles having already
        // added this id, which showed the new role as a duplicate row.
        if (!state.roles.some(role => role.id === newRole.id)) {
          state.roles.push(newRole)
        }
      })
      // Update role
      .addCase(updateRole.fulfilled, (state, action) => {
        const updated = { ...action.payload, id: String(action.payload.id) }
        const index = state.roles.findIndex(role => role.id === updated.id)
        if (index !== -1) {
          state.roles[index] = { ...state.roles[index], ...updated }
        }
      })
      // Delete role
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.roles = state.roles.filter(role => role.id !== action.payload)
      })
  }
})

export default rolesSlice.reducer